import { ReactNode, Suspense, useCallback, useMemo } from "react";
import { useStories } from "../../hooks/CmsHooks";
import { Spinner } from "reactstrap";
import { AnimatePresence, motion } from "motion/react";
import StoryPreview from "../StoryPreview";
import {
  Story,
  StoryCategories,
  StorySubject,
  StoryType
} from "../../types/Story";
import { useMediaQuery } from "react-responsive";
import Carousel from "../Carousel";
import { intersection } from "lodash";
import "./style.scss";

const StoryCarousel = ({
  stories
}: {
  header?: (storiesCount: number) => ReactNode;
  stories: Story[];
}) => {
  const isSmallScreen = useMediaQuery({ maxWidth: "600px" });

  const renderSlide = useCallback(
    (story: object, i: number) => (
      <div className={"carousel-slide"} key={i}>
        <StoryPreview key={(story as Story).path} story={story as Story} />
      </div>
    ),
    []
  );

  return (
    <div className={"story-carousel"}>
      {stories.length > 1 && (
        <Carousel
          direction="horizontal"
          showMarkers={isSmallScreen}
          hideButtons={isSmallScreen}
          centeredButtons
          navType="dots"
          startOverAtEnd
          slidesData={stories}
          renderSlide={renderSlide}
        />
      )}
    </div>
  );
};

const StoryGridInner = ({
  header,
  stories
}: {
  header?: (storiesCount: number) => ReactNode;
  stories: Story[];
}) => {
  const previews = stories.map(story => (
    <motion.div
      role="list"
      layout={true}
      key={story.path}
      initial={{
        opacity: 0,
        scale: 0.8
      }}
      animate={{
        opacity: 1,
        scale: 1
      }}
      exit={{
        scale: 0.8,
        opacity: 0
      }}
      transition={{
        type: "spring",
        damping: 30,
        stiffness: 400,
        mass: 1
      }}
    >
      <StoryPreview story={story} />
    </motion.div>
  ));
  return (
    <>
      {header?.(previews.length)}
      <div className="story-grid">
        <AnimatePresence initial={false}>{previews}</AnimatePresence>
      </div>
    </>
  );
};

const StoryGrid = ({
  header,
  mode = "grid",
  types = ["article"],
  categories,
  subjects,
  tag,
  productCockpitIds,
  limit,
  skipPath
}: {
  header?: (storiesCount: number) => ReactNode;
  mode?: string;
  types?: StoryType[];
  categories?: StoryCategories[];
  subjects?: StorySubject[];
  tag?: string;
  productCockpitIds?: string[];
  limit?: number;
  skipPath?: string;
}) => {
  const _stories: Story[] = useStories();

  const filteredStories = useMemo(() => {
    if (productCockpitIds) {
      return _stories.filter(story =>
        Array.isArray(productCockpitIds)
          ? Array.isArray(story.products) &&
            story.products.findIndex(prod =>
              productCockpitIds.includes(prod._id)
            ) >= 0
          : true
      );
    } else if (types && types.length > 0) {
      return _stories.filter(
        story => intersection(story.type, types).length > 0
      );
    } else {
      return _stories;
    }
  }, [_stories, productCockpitIds, types]);

  let filteredSujects = subjects;
  if (subjects && subjects.length > 0) {
    filteredSujects = subjects.filter(
      subject =>
        filteredStories.findIndex(
          story => story.subject && story.subject === subject
        ) >= 0
    );
  }

  let filteredCategories = categories;
  if (categories && categories.length > 0) {
    filteredCategories = categories.filter(
      category =>
        filteredStories.findIndex(
          story => story.category && story.category === category
        ) >= 0
    );
  }

  const stories = filteredStories
    .filter(story =>
      filteredSujects && filteredSujects.length > 0
        ? story.subject && filteredSujects.includes(story.subject)
        : true
    )
    .filter(story =>
      filteredCategories && filteredCategories.length > 0
        ? story.category && filteredCategories.includes(story.category)
        : true
    )
    .filter(story =>
      tag ? Array.isArray(story.tags) && story.tags.includes(tag) : true
    )
    .filter(story => (skipPath ? story.path !== skipPath : true))
    .slice(0, limit);
  return (
    stories.length > 0 && (
      <>
        {header?.(stories.length)}
        <AnimatePresence initial={false}>
          {mode === "carousel" ? (
            <StoryCarousel stories={stories} />
          ) : (
            <StoryGridInner stories={stories} />
          )}
        </AnimatePresence>
      </>
    )
  );
};

export const CockpitStoryGrid = ({
  types,
  categories,
  subjects,
  tag,
  mode = "grid",
  limit = 6
}: {
  types?: StoryType[];
  categories?: StoryCategories[];
  subjects?: StorySubject[];
  tag?: string;
  mode?: string;
  limit?: number;
}) => {
  return (
    <Suspense fallback={<Spinner />}>
      <StoryGrid
        limit={limit}
        categories={categories}
        subjects={subjects}
        tag={tag}
        types={types}
        mode={mode}
      />
    </Suspense>
  );
};

export default StoryGrid;
